import { useEffect, useState } from "react";
import { Flex, HStack, Text, Box, Image } from "@chakra-ui/react";
import { supabase } from "@/lib/supabaseClient";
import { useAuth } from "@/context/AuthContext";
import { colors } from "@/theme/colors";

// Secondary client co-branding shown next to the primary Hakina lockup.
// Renders nothing (Hakina-only) when the company has no branding configured
// or the logo fails to load. Branding lives only in React state — never in
// localStorage or the PWA cache — so it can't leak across tenants.
export const CompanyBrand = () => {
  const { activeCompany, isSuperAdmin } = useAuth();
  const [logoUrl, setLogoUrl] = useState(null);
  const [broken, setBroken] = useState(false);
  const path = activeCompany?.logo_path || null;

  useEffect(() => {
    setBroken(false);
    setLogoUrl(null);
    if (!path) return;
    let alive = true;
    supabase.storage.from("company-logos").createSignedUrl(path, 3600)
      .then(({ data }) => { if (alive && data?.signedUrl) setLogoUrl(data.signedUrl); })
      .catch(() => {});
    return () => { alive = false; };
  }, [path]);

  // The acting company is always visible in the header — falling back to the
  // company name when no display branding is configured. Super-Admins (no
  // acting company) see "All companies" instead.
  if (!activeCompany && isSuperAdmin) {
    return (
      <HStack gap="2" pl="3" ml="1" borderLeft="1px solid" borderColor={colors.border} minW="0" data-testid="company-brand">
        <Text fontSize="xs" fontWeight="600" color={colors.textPrimary} whiteSpace="nowrap" data-testid="company-brand-name">
          All companies
        </Text>
      </HStack>
    );
  }
  if (!activeCompany) return null;
  const name = activeCompany.display_name || activeCompany.name;
  const showLogo = logoUrl && !broken;

  return (
    <HStack gap="2" pl="3" ml="1" borderLeft="1px solid" borderColor={colors.border} minW="0" overflow="hidden" data-testid="company-brand">
      {showLogo && (
        <Flex align="center" justify="center" h="34px" maxW={{ base: "56px", md: "90px" }} flexShrink={0} overflow="hidden">
          <Image src={logoUrl} alt={name} maxH="34px" maxW={{ base: "56px", md: "90px" }} objectFit="contain"
            onError={() => setBroken(true)} data-testid="company-brand-logo" />
        </Flex>
      )}
      <Text fontSize="xs" color={colors.textSecondary} whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis"
        minW="0" maxW={{ base: "150px", sm: "160px", md: "240px" }} data-testid="company-brand-name">
        <Box as="span" display={{ base: "none", sm: "inline" }}>for </Box>
        <Box as="span" fontWeight="600" color={colors.textPrimary}>{name}</Box>
      </Text>
    </HStack>
  );
};
